import {
  AccionElemento, CampoEnfrentado, ComparacionDuplicados, EleccionCampo, ElementoMultivalor, FichaSenales,
  FusionPendiente, InformeFusion, ReferenciaPendiente, ReglaCampo, ResultadoCuentaWeb, ResultadoEliminacion, ResultadoFusion,
  TipoDato, TraspasoTabla, ValorEditado,
} from '../../../api/clientesDuplicadosApi';

/**
 * Lógica pura del asistente de duplicados (GESCL-T07 §4-§5). Sin React: es lo que se prueba
 * en `reglas.test.ts`. Todo lo que aquí se calcula lo vuelve a evaluar el servidor al simular
 * y al aplicar (D-GC-34, D-GC-37): esto solo decide qué se enseña.
 */

export const claveCampo = (c: { tabla: string; campo: string }) => `${c.tabla}.${c.campo}`;

const vacio = (v: string | null | undefined) => v === null || v === undefined || v === '';

const numero = (v: string | null) => {
  if (vacio(v)) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Premarca por regla (§5), copia fiel del `BT_PREMARCA_ELIMINA` de `FC_pr_CLIENTE_DUP_COMPARAR`
 * (script 020). Solo se usa cuando el operador intercambia las columnas en el caso LIBRE: el
 * servidor coloca las columnas él solo y no admite un orden distinto, así que la vista
 * intercambiada se recalcula aquí con la misma regla. En cualquier otro caso manda lo que
 * devuelve el servidor.
 */
export const premarcaPorRegla = (regla: ReglaCampo, valorConserva: string | null, valorElimina: string | null): boolean => {
  if (regla === 'NUNCA' || regla === 'DERIVADO' || regla === 'SOLO_LECTURA') return false;
  if (vacio(valorElimina)) return false;
  switch (regla) {
    case 'CONCATENAR':
      return vacio(valorConserva) || !valorConserva!.includes(valorElimina!);
    case 'MAS_ANTIGUA':
      return vacio(valorConserva) || valorElimina! < valorConserva!;
    case 'CERO_ES_DATO':
      return vacio(valorConserva);
    case 'VACIO_SI_NO_POSITIVO': {
      const c = numero(valorConserva);
      const e = numero(valorElimina);
      return (vacio(valorConserva) || (c !== null && c <= 0)) && (e === null ? true : e > 0);
    }
    default:
      return vacio(valorConserva);
  }
};

const normalizarNif = (v: string | null) => {
  if (vacio(v)) return null;
  const n = v!.toUpperCase().replace(/[-. ]/g, '');
  return n === '' ? null : n;
};

/**
 * Vista intercambiada de una comparación (caso LIBRE, §4.3): lo que era conserva pasa a ser
 * elimina y viceversa. Las señales/comparación/alarmas son simétricas; la premarca no, y se
 * recalcula con `premarcaPorRegla` — solo para las filas que el motor fusiona campo a campo.
 */
/**
 * Premarca de una fila al dar la vuelta a las columnas. En los campos se recalcula por la regla;
 * en bloques, ficha de compra y preferencias (script 030) el ganador no cambia de sitio: si ganaba
 * la ficha que ahora se conserva, la premarca pasa a false, y si ganaba la que ahora se elimina,
 * a true — siempre que tuviera algo (si las dos están vacías no hay nada que tomar).
 */
const premarcaIntercambiada = (k: CampoEnfrentado, c: ComparacionDuplicados): boolean => {
  if (k.origen === 'CAMPO') return premarcaPorRegla(k.regla, k.valorElimina, k.valorConserva);
  if (k.premarcaElimina) return false;
  if (k.origen === 'BLOQUE') {
    return c.campos.some((x) => x.origen === 'BLOQUE' && x.tablaBloque === k.tablaBloque && !vacio(x.valorConserva));
  }
  if (k.origen === 'INFORMATIVO' && k.tabla === 'GC_CLTE_FICHA_COMPRA_INVERSOR') {
    return c.fichas.find((f) => f.lado === 1)?.fichaCompraVigente ?? false;
  }
  if (k.origen === 'LISTA' && (k.campo === 'PREF_PROVINCIA' || k.campo === 'PREF_TRAMO')) {
    return c.fichas.find((f) => f.lado === 1)?.fichaCompraVigente ?? false;
  }
  return false;
};

export const intercambiarComparacion = (c: ComparacionDuplicados): ComparacionDuplicados => {
  const otroLado = (l: 1 | 2): 1 | 2 => (l === 1 ? 2 : 1);
  const fichas: FichaSenales[] = c.fichas
    .map((f) => ({ ...f, lado: otroLado(f.lado) }))
    .sort((a, b) => a.lado - b.lado);
  const conserva = fichas.find((f) => f.lado === 1);
  const campos: CampoEnfrentado[] = c.campos.map((k) => {
    const valorConserva = k.valorElimina;
    const valorElimina = k.valorConserva;
    const cmpC = k.campo === 'DS_NIF' ? normalizarNif(valorConserva) : (vacio(valorConserva) ? null : valorConserva);
    const cmpE = k.campo === 'DS_NIF' ? normalizarNif(valorElimina) : (vacio(valorElimina) ? null : valorElimina);
    return {
      ...k,
      valorConserva,
      valorElimina,
      descripcionConserva: k.descripcionElimina,
      descripcionElimina: k.descripcionConserva,
      premarcaElimina: premarcaIntercambiada(k, c),
      // Simétricos, pero se recalculan por si acaso: solo dependen de los dos valores.
      avisoClave: k.clave && cmpC !== null && cmpE !== null && cmpC !== cmpE,
      claveCoincide: k.clave && cmpC !== null && cmpC === cmpE,
    };
  });
  const multivalor: ElementoMultivalor[] = c.multivalor.map((m) => ({ ...m, lado: otroLado(m.lado) }));
  return {
    ...c,
    fichas,
    campos,
    multivalor,
    regla: { ...c.regla, idClienteConservar: conserva?.idCliente ?? c.regla.idClienteConservar },
  };
};

/** Marca de fila del paso 2 (§4.2). */
export type MarcaFila = 'IGUAL' | 'DIFIERE' | 'SOLO_UNO' | 'VACIOS';

export const marcaFila = (k: CampoEnfrentado): MarcaFila => {
  switch (k.comparacion) {
    case 'IGUAL': return 'IGUAL';
    case 'DIFIERE': return 'DIFIERE';
    case 'SOLO_CONSERVA':
    case 'SOLO_ELIMINA': return 'SOLO_UNO';
    default: return 'VACIOS';
  }
};

/** Filas que llevan dato: las de lista solo marcan su sitio en el orden. */
const esFilaDeDato = (k: CampoEnfrentado) => k.origen !== 'LISTA';

export interface ContadoresVerificacion {
  coinciden: number;
  difieren: number;
  soloUno: number;
  alarmas: number;
  textoSucio: number;
  clavesCoinciden: number;
  avisosClave: number;
  /**
   * Etiquetas de las claves externas que coinciden. Pueden venir de filas que no se pintan
   * (`Id Auraquantic`, `Id Smartup` no están en el set acordado), y aun así son la prueba más
   * fuerte de que las dos fichas son la misma persona: la pareja 116419/116412 se verificó así.
   */
  etiquetasClaveCoincide: string[];
}

/**
 * Contadores del paso 2. Lo que el operador ve (coinciden / difieren / solo en una) se cuenta
 * sobre las filas visibles; las señales se cuentan sobre todas, incluidas las ocultas.
 */
export const contarVerificacion = (campos: CampoEnfrentado[]): ContadoresVerificacion => {
  const visibles = campos.filter((k) => k.mostrar && esFilaDeDato(k));
  const todas = campos.filter(esFilaDeDato);
  return {
    coinciden: visibles.filter((k) => k.comparacion === 'IGUAL').length,
    difieren: visibles.filter((k) => k.comparacion === 'DIFIERE').length,
    soloUno: visibles.filter((k) => k.comparacion === 'SOLO_CONSERVA' || k.comparacion === 'SOLO_ELIMINA').length,
    alarmas: todas.filter((k) => k.alarmaActiva).length,
    textoSucio: todas.filter((k) => k.textoSucio).length,
    clavesCoinciden: todas.filter((k) => k.claveCoincide).length,
    avisosClave: todas.filter((k) => k.avisoClave).length,
    etiquetasClaveCoincide: todas.filter((k) => k.claveCoincide).map((k) => k.etiqueta),
  };
};

// ─── Edición de la columna izquierda (paso 4, script 030) ───────
//
// La izquierda es lo que queda. Antes de tocar nada enseña la PREMARCA (la regla, también en
// bloques, ficha de compra y preferencias: el servidor la calcula igual que el motor). El
// operador escribe encima, y «restaurar» vuelve a la premarca. Todo lo tocado vive en
// `EdicionFusion`, que solo guarda lo tocado; el resto se deriva de la comparación.

export type TipoListaEditable = 'EMAIL' | 'TELEFONO';
export type TipoPreferencia = 'PREF_PROVINCIA' | 'PREF_TRAMO';

/** Lo que el operador ha hecho con un email o un teléfono de la lista fusionada. */
export interface CambioElemento {
  eliminado?: boolean;
  /**
   * Email o teléfono del duplicado pasado a la izquierda (con su flecha, o con «aplicar reglas»).
   * Nada del duplicado entra solo en la lista que queda (Dev, 2026-09-24): cada columna empieza
   * con lo suyo y el operador elige qué trae.
   */
  anadido?: boolean;
  valor?: string;
  idTipo?: number;
}

/** Estado de edición del paso 4: solo lo tocado. */
export interface EdicionFusion {
  /** `tabla.campo` → valor escrito (null = vaciar). */
  valores: Record<string, string | null>;
  /** `tipo:idElemento` → cambio. */
  elementos: Record<string, CambioElemento>;
  /** Principal elegido por tipo; ausente = el del superviviente; null = ninguno. */
  principal: Partial<Record<TipoListaEditable, number | null>>;
  /** Conjunto final de ids de catálogo por preferencia; ausente = el del superviviente. */
  preferencias: Partial<Record<TipoPreferencia, number[]>>;
}

export const EDICION_VACIA: EdicionFusion = { valores: {}, elementos: {}, principal: {}, preferencias: {} };

export const claveElemento = (tipo: string, idElemento: number) => `${tipo}:${idElemento}`;

/** Igualdad de dos valores de celda: vacío = vacío; las fechas, por su día. */
export const mismoValor = (a: string | null | undefined, b: string | null | undefined, tipoDato?: TipoDato | null): boolean => {
  if (vacio(a) && vacio(b)) return true;
  if (vacio(a) || vacio(b)) return false;
  if (tipoDato === 'FECHA' || tipoDato === 'FECHA_HORA') return a!.slice(0, 10) === b!.slice(0, 10);
  return a === b;
};

/**
 * Lo que haría el motor si el operador no dijera nada: la premarca de las reglas de dato
 * ganador (§5). Ya NO es lo que enseña la izquierda al empezar (Dev, 2026-09-24): es lo que
 * aplica el botón «aplicar reglas», y la referencia contra la que se calcula qué viaja.
 */
export const valorPremarca = (k: CampoEnfrentado) => (k.premarcaElimina ? k.valorElimina : k.valorConserva);
export const descripcionPremarca = (k: CampoEnfrentado) => (k.premarcaElimina ? k.descripcionElimina : k.descripcionConserva);

/**
 * Lo que enseña la izquierda al empezar. Cada columna, lo suyo: el valor del superviviente.
 * Salvo en lo que el operador no puede tocar (identificadores, calculados, fechas de la ficha
 * de compra): ahí se enseña lo que de verdad va a quedar, porque no hay forma de cambiarlo y
 * enseñar otra cosa sería engañar (el Id web, por ejemplo, se copia del duplicado si el
 * superviviente no tiene).
 */
export const valorInicial = (k: CampoEnfrentado): string | null => (k.editable ? k.valorConserva : valorPremarca(k));

/** Valor actual de la celda izquierda: lo escrito, o el inicial. */
export const valorActual = (k: CampoEnfrentado, e: EdicionFusion): string | null => {
  const propio = e.valores[claveCampo(k)];
  return propio === undefined ? valorInicial(k) : propio;
};

export const celdaTocada = (k: CampoEnfrentado, e: EdicionFusion) => e.valores[claveCampo(k)] !== undefined;

/** El nombre completo es la columna calculada de `GC_PERSONA`: no se edita, se deriva. */
export const esNombreCompleto = (k: { tabla: string; campo: string }) =>
  k.tabla === 'GC_PERSONA' && k.campo === 'DS_NOMBRE_COMPLETO';

/**
 * Nombre completo tal como quedará: la misma fórmula que la columna calculada de la base,
 * `CONCAT(DS_NOMBRE, ' ', DS_APELLIDO_1, ' ', DS_APELLIDO_2)` (medido en `sys.computed_columns`,
 * 2026-09-24), aplicada a lo que enseña la izquierda. `CONCAT` convierte los NULL en cadena vacía
 * y deja los separadores, así que un apellido vacío deja su espacio, igual que en la base.
 * Devuelve null si no se encuentra alguno de los tres campos (set sin ellos): entonces se enseña
 * el valor que sirve el servidor.
 */
export const nombreCompletoActual = (campos: CampoEnfrentado[], e: EdicionFusion): string | null => {
  const parte = (campo: string) => campos.find((k) => k.tabla === 'GC_PERSONA' && k.campo === campo);
  const nombre = parte('DS_NOMBRE');
  const ap1 = parte('DS_APELLIDO_1');
  const ap2 = parte('DS_APELLIDO_2');
  if (!nombre || !ap1 || !ap2) return null;
  return [nombre, ap1, ap2].map((k) => valorActual(k, e) ?? '').join(' ');
};

/** Un bloque 1:1 tiene fila en alguna de las dos fichas: hay dónde escribir. */
export const bloqueConDatos = (k: CampoEnfrentado, campos: CampoEnfrentado[]) =>
  campos.some((x) => x.origen === 'BLOQUE' && x.tablaBloque === k.tablaBloque
    && (!vacio(x.valorConserva) || !vacio(x.valorElimina)));

/**
 * La celda admite escribir: lo dice el servidor (`editable`) y, en bloques y ficha de compra,
 * que exista una fila donde escribir (si ninguna ficha tiene el bloque, la edición no tendría
 * destino y el motor la ignoraría).
 */
export const filaEditable = (k: CampoEnfrentado, campos: CampoEnfrentado[], fichas: FichaSenales[]): boolean => {
  if (k.origen === 'LISTA' || !k.mostrar || !k.editable) return false;
  if (k.origen === 'BLOQUE') return bloqueConDatos(k, campos);
  if (k.origen === 'INFORMATIVO') return fichas.some((f) => f.fichaCompraVigente);
  return true;
};

/** Aunque no se pueda escribir, si la premarca tomó el valor del duplicado el original sigue a un clic. */
export const filaDecidible = (k: CampoEnfrentado, campos: CampoEnfrentado[], fichas: FichaSenales[]): boolean =>
  filaEditable(k, campos, fichas)
  || (k.origen !== 'LISTA' && k.mostrar && k.premarcaElimina && k.regla !== 'NUNCA');

// ── Listas ──

const normalizarElemento = (tipo: string, v: string | null) =>
  (tipo === 'EMAIL' ? (v ?? '').trim().toLowerCase() : (v ?? '').replace(/[\s\-.]/g, ''));

/**
 * El elemento del duplicado está en la lista de la izquierda. Solo si el operador lo ha traído
 * (flecha o «aplicar reglas»): ningún email ni teléfono del duplicado entra solo. Los repetidos
 * no entran nunca: ya están en la izquierda, con el mismo valor.
 */
export const delDuplicadoEnLista = (tipo: TipoListaEditable, m: ElementoMultivalor, e: EdicionFusion): boolean => {
  if (m.lado !== 2 || m.repetido) return false;
  return Boolean(e.elementos[claveElemento(tipo, m.idElemento)]?.anadido);
};

/** Un email o teléfono tal como queda en la lista fusionada de la izquierda. */
export interface ElementoFusionado {
  elemento: ElementoMultivalor;
  cambio: CambioElemento;
  principal: boolean;
  /** Valor actual (el escrito, o el original). */
  valor: string | null;
  /** Tipo actual del teléfono (id de `GC_C_TIPO_TELEFONO`). */
  idTipo: number | null;
}

/** Principal al empezar: el del superviviente, o ninguno. Cada columna, lo suyo. */
export const principalPorDefecto = (tipo: TipoListaEditable, multivalor: ElementoMultivalor[]): number | null =>
  multivalor.find((m) => m.tipo === tipo && m.lado === 1 && m.principal)?.idElemento ?? null;

/**
 * Principal según las reglas (lo que pone «aplicar reglas»): el del superviviente; si no tiene,
 * el del duplicado (o su gemelo del superviviente cuando está repetido). Nunca dos.
 */
export const principalSegunReglas = (tipo: TipoListaEditable, multivalor: ElementoMultivalor[]): number | null => {
  const izq = multivalor.filter((m) => m.tipo === tipo && m.lado === 1);
  const p1 = izq.find((m) => m.principal);
  if (p1) return p1.idElemento;
  const p2 = multivalor.find((m) => m.tipo === tipo && m.lado === 2 && m.principal);
  if (!p2) return null;
  if (!p2.repetido) return p2.idElemento;
  const gemelo = izq.find((m) => normalizarElemento(tipo, m.valor) === normalizarElemento(tipo, p2.valor));
  return gemelo ? gemelo.idElemento : null;
};

/** Principal actual: el elegido por el operador, o el de por defecto; nunca uno eliminado. */
export const principalActual = (tipo: TipoListaEditable, multivalor: ElementoMultivalor[], e: EdicionFusion): number | null => {
  const id = e.principal[tipo] !== undefined ? e.principal[tipo]! : principalPorDefecto(tipo, multivalor);
  if (id !== null && e.elementos[claveElemento(tipo, id)]?.eliminado) return null;
  const m = multivalor.find((x) => x.tipo === tipo && x.idElemento === id);
  if (m && m.lado === 2 && !m.repetido && !delDuplicadoEnLista(tipo, m, e)) return null;
  return id;
};

/** La lista que enseña la izquierda: los del superviviente y los del duplicado que se han traído. */
export const listaFusionada = (tipo: TipoListaEditable, multivalor: ElementoMultivalor[], e: EdicionFusion): ElementoFusionado[] => {
  const izq = multivalor.filter((m) => m.tipo === tipo && m.lado === 1);
  const der = multivalor.filter((m) => m.tipo === tipo && delDuplicadoEnLista(tipo, m, e));
  const principal = principalActual(tipo, multivalor, e);
  return [...izq, ...der].map((m) => {
    const cambio = e.elementos[claveElemento(tipo, m.idElemento)] ?? {};
    return {
      elemento: m,
      cambio,
      principal: principal === m.idElemento,
      valor: cambio.valor !== undefined ? cambio.valor : m.valor,
      idTipo: cambio.idTipo !== undefined ? cambio.idTipo : m.idCatalogo,
    };
  });
};

/** Preferencias de la premarca: las de la ficha que quedará vigente (D-GC-40). */
export const preferenciasPremarca = (tipo: TipoPreferencia, campos: CampoEnfrentado[], multivalor: ElementoMultivalor[]): number[] => {
  const fila = campos.find((k) => k.origen === 'LISTA' && k.campo === tipo);
  const lado = fila?.premarcaElimina ? 2 : 1;
  return multivalor
    .filter((m) => m.tipo === tipo && m.lado === lado && m.idCatalogo !== null)
    .map((m) => m.idCatalogo!);
};

/** Preferencias del superviviente: lo que enseña la izquierda al empezar. */
export const preferenciasDelSuperviviente = (tipo: TipoPreferencia, multivalor: ElementoMultivalor[]): number[] =>
  multivalor
    .filter((m) => m.tipo === tipo && m.lado === 1 && m.idCatalogo !== null)
    .map((m) => m.idCatalogo!);

export const preferenciasActuales = (tipo: TipoPreferencia, _campos: CampoEnfrentado[], multivalor: ElementoMultivalor[], e: EdicionFusion): number[] =>
  e.preferencias[tipo] ?? preferenciasDelSuperviviente(tipo, multivalor);

// ── Flecha ← : traer el dato de la ficha que se elimina ──

/**
 * El valor del duplicado tal como lo escribiría el editor de la celda. Las fechas llegan del
 * `FOR JSON` con hora y desfase (`1992-01-01T00:00:00.0000000+00:00`) y el editor de fecha
 * escribe solo el día (`1992-01-01`): se copia en ese mismo formato para que la flecha y la
 * edición a mano dejen exactamente lo mismo.
 */
export const valorDelDuplicado = (k: CampoEnfrentado): string | null => {
  if (vacio(k.valorElimina)) return null;
  return k.tipoDato === 'FECHA' || k.tipoDato === 'FECHA_HORA' ? k.valorElimina!.slice(0, 10) : k.valorElimina;
};

/**
 * La flecha tiene sentido: la celda se puede escribir, el duplicado tiene dato y ese dato no
 * es ya lo que queda. Copiarlo es lo mismo que escribirlo a mano: queda como elección del
 * operador, se puede seguir editando encima y «restaurar» vuelve a la sugerencia.
 */
export const puedeTraerDelDuplicado = (k: CampoEnfrentado, e: EdicionFusion, seEdita: boolean): boolean =>
  seEdita && !vacio(k.valorElimina) && !mismoValor(valorActual(k, e), k.valorElimina, k.tipoDato);

/** Un email o teléfono del duplicado que el operador ha quitado de la lista que queda. */
export const elementoRetirado = (tipo: TipoListaEditable, idElemento: number, e: EdicionFusion): boolean =>
  Boolean(e.elementos[claveElemento(tipo, idElemento)]?.eliminado);

/** Preferencias de la ficha del duplicado (ids de catálogo), en su orden. */
export const preferenciasDelDuplicado = (tipo: TipoPreferencia, multivalor: ElementoMultivalor[]): number[] =>
  multivalor
    .filter((m) => m.tipo === tipo && m.lado === 2 && m.idCatalogo !== null)
    .map((m) => m.idCatalogo!);

/** Los dos conjuntos de preferencias son el mismo, sin importar orden ni repetidos. */
export const mismasPreferencias = (a: number[], b: number[]): boolean => {
  const x = new Set(a);
  const y = new Set(b);
  return x.size === y.size && Array.from(x).every((id) => y.has(id));
};

/**
 * Las preferencias de compra son un BLOQUE (Dev, 2026-09-24): van con su ficha, y se quedan las
 * de la ficha más reciente, que es la que sigue vigente (D-GC-40) y la que ya enseña la
 * sugerencia. La flecha no suma: SUSTITUYE todo el conjunto de la izquierda por el del
 * duplicado. Solo tiene sentido si el duplicado tiene alguna y la izquierda no tiene ya esas.
 */
export const puedeTraerPreferencias = (actuales: number[], delDuplicado: number[], seEdita: boolean): boolean =>
  seEdita && delDuplicado.length > 0 && !mismasPreferencias(actuales, delDuplicado);

/**
 * Lo que la flecha deja en la edición: el conjunto del duplicado, o nada (`undefined`) si ese
 * conjunto es justo el del superviviente, para que no cuente como cambio del operador.
 */
export const preferenciasTrasFlecha = (delDuplicado: number[], delSuperviviente: number[]): number[] | undefined => {
  const destino = delDuplicado.filter((id, i, a) => a.indexOf(id) === i);
  return mismasPreferencias(destino, delSuperviviente) ? undefined : destino;
};

// ── «Aplicar reglas»: todo lo que no se ha tocado, según las reglas de dato ganador ──

/**
 * Pasa a la izquierda, de una vez, todo lo que las reglas de dato ganador (§5) toman del
 * duplicado, SOLO en lo que el operador no ha tocado: lo que ya decidió a mano se respeta.
 * Cada cosa pasada queda como si se hubiera traído con su flecha, así que conserva su botón de
 * vuelta atrás al valor original.
 * - Campos: donde la premarca toma el valor del duplicado.
 * - Bloques (dirección, exploraciones): donde la regla del bloque toma el del duplicado.
 * - Emails y teléfonos: se suman los del duplicado que no estén repetidos.
 * - Principal: si el superviviente no tiene, el que dicen las reglas.
 * - Preferencias: las de la ficha que quedará vigente (la más reciente, D-GC-40).
 */
export const aplicarReglas = (c: ComparacionDuplicados, e: EdicionFusion): EdicionFusion => {
  const valores = { ...e.valores };
  for (const k of c.campos) {
    if (k.origen === 'LISTA' || !k.mostrar || !k.editable || !k.premarcaElimina) continue;
    if (valores[claveCampo(k)] !== undefined) continue;
    if (mismoValor(k.valorElimina, k.valorConserva, k.tipoDato)) continue;
    valores[claveCampo(k)] = valorDelDuplicado(k);
  }

  const elementos = { ...e.elementos };
  const principal = { ...e.principal };
  for (const tipo of ['EMAIL', 'TELEFONO'] as TipoListaEditable[]) {
    for (const m of c.multivalor.filter((x) => x.tipo === tipo && x.lado === 2 && !x.repetido)) {
      const clave = claveElemento(tipo, m.idElemento);
      if (elementos[clave] === undefined) elementos[clave] = { anadido: true };
    }
    if (principal[tipo] === undefined && principalPorDefecto(tipo, c.multivalor) === null) {
      const segunReglas = principalSegunReglas(tipo, c.multivalor);
      if (segunReglas !== null) principal[tipo] = segunReglas;
    }
  }

  const preferencias = { ...e.preferencias };
  for (const tipo of ['PREF_PROVINCIA', 'PREF_TRAMO'] as TipoPreferencia[]) {
    if (preferencias[tipo] !== undefined) continue;
    const segunReglas = preferenciasPremarca(tipo, c.campos, c.multivalor);
    if (!mismasPreferencias(segunReglas, preferenciasDelSuperviviente(tipo, c.multivalor))) preferencias[tipo] = segunReglas;
  }

  return { valores, elementos, principal, preferencias };
};

/** Cuántas cosas cambiaría «aplicar reglas» ahora mismo (0 = el botón no tiene nada que hacer). */
export const cambiosDeReglas = (c: ComparacionDuplicados, e: EdicionFusion): number => {
  const d = aplicarReglas(c, e);
  return Object.keys(d.valores).length - Object.keys(e.valores).length
    + Object.keys(d.elementos).length - Object.keys(e.elementos).length
    + Object.keys(d.principal).length - Object.keys(e.principal).length
    + Object.keys(d.preferencias).length - Object.keys(e.preferencias).length;
};

// ── Resumen y petición ──

export interface ResumenEdicion {
  camposTocados: number;
  elementosTocados: number;
  /** Nada que enviar aparte de la regla. */
  sinCambios: boolean;
}

export const resumirEdicion = (e: EdicionFusion): ResumenEdicion => {
  const camposTocados = Object.keys(e.valores).length;
  const elementosTocados = Object.values(e.elementos).filter((c) => c.eliminado || c.anadido || c.valor !== undefined || c.idTipo !== undefined).length
    + Object.keys(e.principal).length
    + Object.keys(e.preferencias).length;
  return { camposTocados, elementosTocados, sinCambios: camposTocados + elementosTocados === 0 };
};

export interface CuerpoEdicion {
  elecciones: EleccionCampo[];
  valores: ValorEditado[];
  elementos: AccionElemento[];
}

/**
 * Lo que viaja al servidor (D-GC-42, script 030): solo lo que difiere de la premarca.
 * - Un campo que vuelve al valor de una de las dos fichas viaja como elección de lado (así el
 *   registro lo apunta como `OPERADOR` sin inventar un valor); cualquier otro, como valor editado.
 * - Un bloque entero devuelto a una ficha viaja como `*`; si se mezcla, los campos que difieren
 *   de la premarca viajan como valores y el motor los escribe sobre la fila que queda.
 * - El principal de email y teléfono viaja siempre que haya lista: al sumar dos fichas quedarían
 *   dos principales, y la izquierda enseña uno solo.
 * - Las preferencias viajan como diferencia contra las de la ficha que quedará vigente.
 */
export const construirEdicion = (c: ComparacionDuplicados, e: EdicionFusion): CuerpoEdicion => {
  const elecciones: EleccionCampo[] = [];
  const valores: ValorEditado[] = [];
  const elementos: AccionElemento[] = [];
  const campos = c.campos.filter((k) => k.origen !== 'LISTA');

  /* Lo que queda es lo que enseña la izquierda, y la izquierda empieza con lo del superviviente,
     no con la premarca. Así que viaja todo lo que difiera de la premarca, lo haya tocado el
     operador o no: si la regla tomaría el dato del duplicado y la izquierda sigue con el del
     superviviente, eso también es una decisión y tiene que viajar (si no, el motor aplicaría la
     regla y quedaría algo distinto de lo que se ve). Lo no editable no viaja: el motor manda. */
  for (const k of campos.filter((x) => (x.origen === 'CAMPO' || x.origen === 'INFORMATIVO') && x.editable)) {
    const propio = valorActual(k, e);
    if (mismoValor(propio, valorPremarca(k), k.tipoDato)) continue;
    if (k.origen === 'CAMPO') {
      if (mismoValor(propio, k.valorConserva, k.tipoDato)) { elecciones.push({ tabla: k.tabla, campo: k.campo, tomarDelDuplicado: false }); continue; }
      if (mismoValor(propio, k.valorElimina, k.tipoDato)) { elecciones.push({ tabla: k.tabla, campo: k.campo, tomarDelDuplicado: true }); continue; }
    }
    valores.push({ tabla: k.tabla, campo: k.campo, valor: vacio(propio) ? null : propio });
  }

  const tablas = Array.from(new Set(campos.filter((k) => k.origen === 'BLOQUE' && k.tablaBloque).map((k) => k.tablaBloque!)));
  for (const tabla of tablas) {
    const filas = campos.filter((k) => k.origen === 'BLOQUE' && k.tablaBloque === tabla && k.editable);
    if (filas.length === 0) continue;
    const ladoElimina = filas[0].premarcaElimina;
    const finales = filas.map((k) => ({ k, v: valorActual(k, e) }));
    if (finales.every(({ k, v }) => mismoValor(v, valorPremarca(k), k.tipoDato))) continue;
    const todoConserva = finales.every(({ k, v }) => mismoValor(v, k.valorConserva, k.tipoDato));
    const todoElimina = finales.every(({ k, v }) => mismoValor(v, k.valorElimina, k.tipoDato));
    if (todoConserva && ladoElimina) { elecciones.push({ tabla, campo: '*', tomarDelDuplicado: false }); continue; }
    if (todoElimina && !ladoElimina) { elecciones.push({ tabla, campo: '*', tomarDelDuplicado: true }); continue; }
    for (const { k, v } of finales) {
      if (!mismoValor(v, valorPremarca(k), k.tipoDato)) valores.push({ tabla: k.tabla, campo: k.campo, valor: vacio(v) ? null : v });
    }
  }

  for (const tipo of ['EMAIL', 'TELEFONO'] as TipoListaEditable[]) {
    const lista = listaFusionada(tipo, c.multivalor, e);
    /* El motor traspasa todos los teléfonos del duplicado (estrategia LIBRE). Los que el
       operador no ha traído a la izquierda se le piden eliminar, para que lo que queda sea
       exactamente lo que se ve. Los repetidos no hace falta: el motor ya los descarta. */
    for (const m of c.multivalor.filter((x) => x.tipo === tipo && x.lado === 2 && !x.repetido)) {
      if (!delDuplicadoEnLista(tipo, m, e)) elementos.push({ tipo, accion: 'ELIMINAR', idElemento: m.idElemento });
    }
    for (const x of lista) {
      const id = x.elemento.idElemento;
      if (x.cambio.eliminado) { elementos.push({ tipo, accion: 'ELIMINAR', idElemento: id }); continue; }
      const valorCambiado = x.cambio.valor !== undefined && !mismoValor(x.cambio.valor, x.elemento.valor);
      const tipoCambiado = x.cambio.idTipo !== undefined && x.cambio.idTipo !== x.elemento.idCatalogo;
      if (valorCambiado || tipoCambiado) {
        elementos.push({
          tipo, accion: 'EDITAR', idElemento: id,
          valor: valorCambiado ? x.cambio.valor : null,
          idTipo: tipoCambiado ? x.cambio.idTipo : null,
        });
      }
    }
    if (lista.some((x) => !x.cambio.eliminado)) {
      elementos.push({ tipo, accion: 'PRINCIPAL', idElemento: principalActual(tipo, c.multivalor, e) });
    }
  }

  for (const tipo of ['PREF_PROVINCIA', 'PREF_TRAMO'] as TipoPreferencia[]) {
    // Contra la ficha que quedará vigente, que es sobre la que escribe el motor.
    const propio = preferenciasActuales(tipo, c.campos, c.multivalor, e);
    const base = preferenciasPremarca(tipo, c.campos, c.multivalor);
    for (const id of propio) if (!base.includes(id)) elementos.push({ tipo, accion: 'ANADIR', idElemento: id });
    for (const id of base) if (!propio.includes(id)) elementos.push({ tipo, accion: 'QUITAR', idElemento: id });
  }

  return { elecciones, valores, elementos };
};

// ─── Presentación de valores ────────────────────────────────────

/** Fecha ISO tal como la escribe `FOR JSON`, con o sin hora y con o sin desfase horario. */
const ISO_FECHA = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

/**
 * Los valores llegan como texto (`FOR JSON` del SP): fechas ISO, bits `true`/`false`, números.
 *
 * La fecha se lee tal como está escrita, sin convertirla a la zona del navegador. Las columnas
 * `datetimeoffset` llegan con su desfase (`...T00:00:00.0000000+00:00`) y pasarlas por la hora
 * local corre la hora —y con ella, a veces, el día—, que es justo lo que no puede pasar cuando
 * lo que se está haciendo es comparar dos fichas dato a dato.
 */
export const formatearValor = (valor: string | null, descripcion?: string | null): string => {
  if (descripcion) return descripcion;
  if (vacio(valor)) return '';
  const v = valor!;
  if (v === 'true') return 'Sí';
  if (v === 'false') return 'No';
  const m = ISO_FECHA.exec(v);
  if (m) {
    const [, anio, mes, dia, hh, mm, ss] = m;
    const fecha = `${dia}/${mes}/${anio}`;
    if (hh === undefined) return fecha;
    return hh === '00' && mm === '00' && ss === '00' ? fecha : `${fecha} ${hh}:${mm}`;
  }
  return v;
};

// ─── Informe del tratamiento (§4.5) ─────────────────────────────

/** Nombres en el lenguaje del operador para las tablas más frecuentes del traspaso. */
const NOMBRES_TABLA: Record<string, string> = {
  GC_ACTIVIDAD_CLIENTES: 'actividades',
  GC_PERSONA_RELACIONES: 'relaciones con entidades',
  GC_PERSONAS_ENTIDAD: 'personas de contacto',
  GC_VISITAS_WEB: 'visitas web',
  GC_EMAIL_PERSONA: 'emails',
  GC_TELEFONO_PERSONA: 'teléfonos',
  GC_DIRECCION_PERSONA: 'direcciones',
  GC_CLTE_FICHA_COMPRA_INVERSOR: 'fichas de compra',
  GC_CLIENTE_GARANTIA: 'garantías',
  GC_OPERACION_CLIENTE: 'operaciones',
  GC_OPERACION_SDA_CLIENTE: 'SDAs',
  GC_FDOC_REGISTRO_CLIENTE: 'registros de firma digital',
  FC_ALTA_CASO: 'casos de alta',
  GC_CLIENTE_EXPLORACION_ROC: 'exploraciones ROC',
  GC_CLIENTE_EXPLORACION_VC1: 'exploraciones VC1',
  GC_EPC_PERSONA: 'avisos EPC',
  GC_CLIENTE_CAMPANIA: 'campañas',
  GC_INV_PREFERENCIAS_SUBREGION: 'preferencias de provincia',
  GC_INV_PREFERENCIAS_FACT: 'preferencias de facturación',
};

export const nombreTabla = (tabla: string) => NOMBRES_TABLA[tabla] ?? tabla;

export interface LineaInforme {
  clave: string;
  estado: 'ok' | 'aviso' | 'error' | 'pendiente';
  texto: string;
  detalle?: string[];
}

const agruparTraspasos = (traspasos: TraspasoTabla[]): LineaInforme[] => {
  const porTabla = new Map<string, {
    traspasadas: number; descartadas: number; editados: number; eliminados: number; anadidas: number; quitadas: number; vacias: number; cerrados: number; detalle: string[];
  }>();
  for (const t of traspasos) {
    const acc = porTabla.get(t.tabla)
      ?? { traspasadas: 0, descartadas: 0, editados: 0, eliminados: 0, anadidas: 0, quitadas: 0, vacias: 0, cerrados: 0, detalle: [] };
    // Las pseudo-columnas del motor (script 024/030) cuentan cosas distintas de un traspaso.
    if (t.columna === '(editados)') acc.editados += t.traspasadas;
    else if (t.columna === '(eliminados)') acc.eliminados += t.descartadas;
    else if (t.columna === '(operador)') { acc.anadidas += t.traspasadas; acc.quitadas += t.descartadas; }
    // Fichas de compra vacías del duplicado: no se traspasan, se borran (D-GC-89, script 034).
    else if (t.columna === '(vacias)') acc.vacias += t.descartadas;
    // Casos de alta abiertos del duplicado: se cierran, no se traspasan (D-GC-90, script 035).
    else if (t.columna === '(cerrados)') acc.cerrados += t.descartadas;
    else { acc.traspasadas += t.traspasadas; acc.descartadas += t.descartadas; }
    acc.detalle.push(`${t.columna}: ${t.traspasadas} traspasadas · ${t.descartadas} descartadas`);
    porTabla.set(t.tabla, acc);
  }
  const lineas: LineaInforme[] = [];
  porTabla.forEach((v, tabla) => {
    if (v.traspasadas + v.descartadas + v.editados + v.eliminados + v.anadidas + v.quitadas + v.vacias + v.cerrados === 0) return;
    const partes: string[] = [];
    if (v.traspasadas > 0) partes.push(`Reasignadas ${v.traspasadas} ${nombreTabla(tabla)}`);
    if (v.descartadas > 0) partes.push(`${v.descartadas} descartadas por repetidas${v.traspasadas === 0 ? ` en ${nombreTabla(tabla)}` : ''}`);
    if (v.editados > 0) partes.push(`${v.editados} ${nombreTabla(tabla)} editados por ti`);
    if (v.eliminados > 0) partes.push(`${v.eliminados} ${nombreTabla(tabla)} eliminados por ti`);
    if (v.anadidas > 0 || v.quitadas > 0) partes.push(`${nombreTabla(tabla)}: ${v.anadidas} añadidas y ${v.quitadas} quitadas por ti`);
    if (v.vacias > 0) partes.push(`${v.vacias} ${v.vacias === 1 ? 'ficha de compra vacía' : 'fichas de compra vacías'} del duplicado no traspasada${v.vacias === 1 ? '' : 's'}`);
    if (v.cerrados > 0) partes.push(`${v.cerrados} ${v.cerrados === 1 ? 'caso de alta abierto del duplicado cerrado' : 'casos de alta abiertos del duplicado cerrados'} (quedan como cliente existente actualizado)`);
    lineas.push({ clave: `traspaso.${tabla}`, estado: 'ok', texto: partes.join(' · '), detalle: v.detalle });
  });
  return lineas;
};

/** Líneas de una simulación o de una fusión aplicada (los pasos 1-4 del orden de §4.8). */
export const lineasDeFusion = (r: ResultadoFusion): LineaInforme[] => {
  const lineas: LineaInforme[] = [];
  const conservado = r.codigoConservado ?? `ID ${r.idClienteConservado}`;
  const eliminado = r.codigoEliminado ?? `ID ${r.idClienteEliminado}`;
  const porOperador = r.campos.filter((k) => k.origen === 'OPERADOR').length;
  lineas.push({
    clave: 'campos',
    estado: 'ok',
    texto: `Datos fusionados en ${conservado} (${r.campos.length} campos tomados de ${eliminado}${porOperador ? `, ${porOperador} elegidos por el operador` : ''})`,
    detalle: r.campos.map((k) => `${k.campo}: «${formatearValor(k.valorAntes)}» → «${formatearValor(k.valorDespues)}» (${k.origen})`),
  });
  lineas.push(...agruparTraspasos(r.traspasos));
  if (r.pendientes.length === 0) {
    lineas.push({ clave: 'verificacion', estado: 'ok', texto: 'Verificación: 0 referencias pendientes' });
  } else {
    lineas.push({
      clave: 'verificacion',
      estado: 'error',
      texto: `Quedan ${r.pendientes.length} referencias pendientes — no se ha eliminado nada`,
      detalle: r.pendientes.map((p) => `${p.tabla}.${p.columna}: ${p.filasEliminar} filas${p.observaciones ? ` (${p.observaciones})` : ''}`),
    });
  }
  return lineas;
};

/**
 * La línea del paso de cuenta web, tal como la redacta el servidor. En `NADA` ya no se resume a
 * «nada que resolver»: si el superviviente tenía cuenta, el paso la pone al día (código y email
 * principal) y eso tiene que verse; y si no se encontró en la web, es un aviso.
 */
export const lineaCuentaWeb = (c: ResultadoCuentaWeb): LineaInforme => ({
  clave: 'cuentaWeb',
  estado: /no encontrada/i.test(c.resultado) ? 'aviso' : 'ok',
  texto: c.resultado,
});

export const lineasEliminacion = (e: ResultadoEliminacion, codigoEliminado: string | null): LineaInforme[] => {
  const lineas: LineaInforme[] = [{
    clave: 'eliminado',
    estado: 'ok',
    texto: `Registro ${codigoEliminado ?? ''} (ID ${e.idClienteEliminado}) eliminado`,
  }];
  if (!e.personaEliminada) {
    lineas.push({
      clave: 'persona',
      estado: 'aviso',
      texto: `La persona se conserva: ${e.personaConservadaPor ?? 'sostiene otro rol'}`,
    });
  }
  return lineas;
};

export const lineasPendientes = (pendientes: ReferenciaPendiente[]): LineaInforme => ({
  clave: 'pendientes',
  estado: 'error',
  texto: `Quedan ${pendientes.length} referencias al cliente — no se ha eliminado nada`,
  detalle: pendientes.map((p) => `${p.tabla}.${p.columna}: ${p.filasEliminar} filas`),
});

// ─── Reanudar una fusión aplicada sin terminar (S-03/S-19, GESCL-T08) ───────

/**
 * La fusión sin terminar que corresponde a ESTA pareja en ESTE sentido. Si la pendiente es de otra
 * pareja (comparte uno de los dos clientes) o del sentido contrario, no se reanuda desde aquí:
 * reanudarla con la pantalla montada al revés mezclaría dos fusiones distintas.
 */
export const pendienteDeLaPareja = (
  pendientes: FusionPendiente[], idConservar: number, idEliminar: number,
): FusionPendiente | null =>
  pendientes.find((p) => p.idClienteConservado === idConservar && p.idClienteEliminado === idEliminar) ?? null;

/**
 * El resultado de `aplicar` reconstruido desde el informe de la fusión, para reanudar una que se
 * aplicó pero cuya respuesta se perdió. Solo vale para una APLICADA: lo que queda por hacer
 * (cuenta web y borrado) lo vuelve a comprobar el servidor, y las referencias pendientes de una
 * APLICADA son siempre cero (si no, no se habría aplicado).
 */
export const resultadoDesdeInforme = (i: InformeFusion): ResultadoFusion => ({
  idClienteFusion: i.idClienteFusion,
  idEstado: i.idEstado,
  estado: i.estado,
  confirmado: true,
  idClienteConservado: i.idClienteConservado,
  codigoConservado: i.codigoConservado,
  idClienteEliminado: i.idClienteEliminado,
  codigoEliminado: i.codigoEliminado,
  campos: i.campos,
  traspasos: i.traspasos,
  pendientes: [],
  cuentaWeb: null,
});
